import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { Dispatch, AnyAction } from 'redux';
import { mergeStyles } from "@fluentui/react";
import { IAppState, NavigationTarget, WebchatOptions } from "../../Redux/reduxState";
import { actionTypes, genericSingleAction } from "../../Redux/actions";
import { ContainerName } from "../../Redux/reducers/AppReducer";
import { EditorTab } from "../../utilities/types";
import { CustomizationEntry, CustomizationTab, customizationTabs } from "../customizeEntries";
import { CategoryLink } from "./categoryLink";
import ConfigurationEntryViewer from "./configurationEntryViewer";
import CustomizationSearchBox from "./customizationSearchBox";
import CustomizationSearchResults, { OtherTabResult } from "./customizationSearchResults";
import { isSearchActive, searchEntries } from "./customizationSearch";
import { getOptionValue, isEntryHidden } from "./optionValue";

const columnHeight = {
    height: 'calc(100vh - var(--webchat-editor-chrome-height))',
    maxHeight: 'calc(100vh - var(--webchat-editor-chrome-height))',
};

const categoriesColumn = mergeStyles(
    {
        ...columnHeight,
        overflowY: 'auto',
        overscrollBehavior: 'none',
        scrollbarGutter: 'stable',
        paddingLeft: 'var(--druid-space-24) !important',
        paddingRight: 'var(--druid-space-8) !important',
        maxWidth: '280px',
        paddingTop: 'var(--druid-space-24) !important',
        borderRight: '1px solid var(--druid-color-border)',
    }
);

const categoriesList = mergeStyles(
    {
        display: 'flex',
        flexDirection: 'column',
        rowGap: 'var(--druid-space-2)',
    }
);

const contentColumn = mergeStyles(
    {
        ...columnHeight,
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: '25px !important',
        paddingRight: '0 !important',
        '.ms-Checkbox.is-checked': {
            '.ms-Checkbox-checkbox': {
                background: 'var(--druid-color-success-primary)',
                borderColor: `var(--druid-color-success-primary) !important`,
            }
        },
        '.ms-Checkbox-checkbox': {
            border: '1px solid var(--druid-color-border)',
        },
        '.ms-TextField-fieldGroup': {
            borderRadius: 4,
            border: '1px solid var(--druid-color-border)',
            ':hover, ::after': {
                border: `1px solid var(--druid-color-success-primary)`,
                borderRadius: 4
            },
            '.ms-TextField-prefix': {
                borderRadius: 4
            }
        },
        '.ms-Dropdown': {
            '.ms-Dropdown-title': {
                borderRadius: 4,
                border: '1px solid var(--druid-color-border)',
            },
            '::after': {
                border: `2px solid var(--druid-color-success-primary)`,
                borderRadius: 4
            },
        },
        '.ms-Button--primary': {
            background: 'var(--druid-color-success-primary)',
            borderColor: `var(--druid-color-success-primary) !important`,
            ':hover, :active': {
                background: 'var(--druid-color-success-hover)',
                borderColor: `var(--druid-color-success-hover) !important`
            }
        }
    }
);

const contentScroll = mergeStyles(
    {
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        overscrollBehavior: 'none',
        scrollbarGutter: 'stable',
        paddingTop: 'var(--druid-space-8)',
        paddingRight: 'var(--druid-space-8)',
    }
);

interface StateProps {
    activeCategory?: string;
    searchQuery: string;
    navigationTarget?: NavigationTarget;
    webchatOptions: WebchatOptions;
}

interface DispatchProps {
    updateStyleElement: (styleElementName: string, value: any, containerName?: ContainerName) => void;
    selectCategory: (tab: EditorTab, category: string) => void;
    setSearchQuery: (tab: EditorTab, query: string) => void;
    navigateTo: (target: NavigationTarget) => void;
    moveSearchToTab: (from: EditorTab, to: EditorTab, query: string) => void;
}

interface Props {
    tab: CustomizationTab;
}

type PropsType = StateProps & DispatchProps & Props;

export const CustomizationTabPanel = (props: PropsType) => {
    const { tab, activeCategory, searchQuery, navigationTarget, webchatOptions, updateStyleElement } = props;

    const getStyleOptionValue = useCallback(
        (key: any, containerName?: ContainerName) => getOptionValue(webchatOptions, key, containerName),
        [webchatOptions]
    );

    const searching = isSearchActive(searchQuery);

    const results = useMemo(
        () => searching ? searchEntries(tab.entries, searchQuery).filter(entry => !isEntryHidden(entry, getStyleOptionValue)) : [],
        [searching, tab, searchQuery, getStyleOptionValue]
    );

    const matchCounts = useMemo(() => results.reduce((counts, entry) => {
        counts[entry.category] = (counts[entry.category] || 0) + 1;
        return counts;
    }, {} as Record<string, number>), [results]);

    const otherTabResults = useMemo((): OtherTabResult[] => searching
        ? customizationTabs
            .filter(other => other.key !== tab.key)
            .map(other => ({ tab: other, count: searchEntries(other.entries, searchQuery).filter(entry => !isEntryHidden(entry, getStyleOptionValue)).length }))
            .filter(other => other.count > 0)
        : [], [searching, tab, searchQuery, getStyleOptionValue]);

    const categoryEntries = useMemo(
        () => tab.entries.filter(entry => entry.category === activeCategory),
        [tab, activeCategory]
    );

    const onNavigate = (entry: CustomizationEntry, openSubCategory: boolean) => props.navigateTo({
        tab: tab.key,
        category: entry.category,
        subCategory: openSubCategory ? entry.subCategory : undefined,
        entryId: entry.id,
    });

    const renderContent = () => {
        if (searching) {
            return <CustomizationSearchResults
                tab={tab}
                query={searchQuery}
                results={results}
                otherTabResults={otherTabResults}
                getStyleOptionValue={getStyleOptionValue}
                updateStyleElement={updateStyleElement}
                onNavigate={onNavigate}
                onSearchInTab={other => props.moveSearchToTab(tab.key, other.key, searchQuery)}
            />;
        }

        if (!activeCategory) {
            return <p><strong>There are no {tab.text} options yet.</strong></p>;
        }

        return <ConfigurationEntryViewer
            category={activeCategory}
            entries={categoryEntries}
            webchatOptions={webchatOptions}
            navigationTarget={navigationTarget}
            updateStyleElement={updateStyleElement}
            getStyleOptionValue={getStyleOptionValue}
        />;
    };

    return (
        <div className="ms-lg12">
            <div className={`ms-Grid-col ms-sm3 ms-md3 ms-lg3 ${categoriesColumn}`}>
                <div role="tablist" aria-orientation="vertical" aria-label={`${tab.text} groups`} className={categoriesList}>
                    {tab.categories.map(category => (
                        <CategoryLink
                            key={category}
                            name={category}
                            active={!searching && category === activeCategory}
                            matchCount={searching ? matchCounts[category] || 0 : undefined}
                            onSelect={() => props.selectCategory(tab.key, category)}
                        />
                    ))}
                </div>
            </div>
            <div className={`ms-Grid-col ms-sm9 ms-md9 ms-lg9 ${contentColumn}`}>
                <CustomizationSearchBox
                    value={searchQuery}
                    placeholder={`Search ${tab.text} options`}
                    onChange={query => props.setSearchQuery(tab.key, query)}
                />
                <div className={contentScroll}>
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

////////////////// Redux //////////////////

const mapStateToProps = (state: IAppState, ownProps: Props): StateProps => ({
    activeCategory: state.activeCategories[ownProps.tab.key],
    searchQuery: state.searchQueries[ownProps.tab.key] || '',
    navigationTarget: state.navigationTarget?.tab === ownProps.tab.key ? state.navigationTarget : undefined,
    webchatOptions: state.webchatOptions,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): DispatchProps => ({
    updateStyleElement: (styleElementName: string, value: any, containerName?: ContainerName) => {
        dispatch(genericSingleAction<any>(actionTypes.UPDATE_STYLE_ELEMENT, { styleElementName: styleElementName, value: value, containerName: containerName }));
    },
    selectCategory: (tab: EditorTab, category: string) => {
        dispatch(genericSingleAction<any>(actionTypes.SELECT_CUSTOMIZATION_CATEGORY, { tab, category }));
    },
    setSearchQuery: (tab: EditorTab, query: string) => {
        dispatch(genericSingleAction<any>(actionTypes.SET_CUSTOMIZATION_SEARCH, { tab, query }));
    },
    navigateTo: (target: NavigationTarget) => {
        dispatch(genericSingleAction<any>(actionTypes.NAVIGATE_TO_ENTRY, target));
    },
    // The tab left behind goes back to its group instead of keeping an empty result list.
    moveSearchToTab: (from: EditorTab, to: EditorTab, query: string) => {
        dispatch(genericSingleAction<any>(actionTypes.SET_CUSTOMIZATION_SEARCH, { tab: from, query: '' }));
        dispatch(genericSingleAction<any>(actionTypes.SELECT_EDITOR_TAB, { tab: to }));
        dispatch(genericSingleAction<any>(actionTypes.SET_CUSTOMIZATION_SEARCH, { tab: to, query }));
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(CustomizationTabPanel);
